'use client';

import { ReactNode, Children, isValidElement, Fragment, memo } from 'react';
import { useEffect } from 'react';

const processChildren = (child: ReactNode) => {
  if (!isValidElement(child)) {
    return;
  }

  // If this is <title>, update document.title
  if (child.type === 'title') {
    document.title = child.props.children;
  }

  // In case of <meta>, update or add corresponding meta tag
  if (child.type === 'meta') {
    const meta = document.querySelector(`meta[name="${child.props.name}"]`);

    if (meta) {
      // Update existing meta tag
      meta.setAttribute('content', child.props.content);
    } else {
      // Create new meta tag
      const newMeta = document.createElement('meta');
      newMeta.setAttribute('name', child.props.name);
      newMeta.setAttribute('content', child.props.content);
      document.head.appendChild(newMeta);
    }
  }

  // In case of <link>, update or add corresponding link tag
  if (child.type === 'link') {
    const link = document.querySelector(`link[rel="${child.props.rel}"][href="${child.props.href}"]`);

    if (!link) {
      // Create new link tag
      const newLink = document.createElement('link');
      Object.entries(child.props).forEach(([key, value]) => {
        if (value) {
          newLink.setAttribute(key, value.toString());
        }
      });
      document.head.appendChild(newLink);
    }
  }
};

export const Head = memo(({ children }: { children: ReactNode }) => {
  useEffect(() => {
    Children.forEach(children, (child: ReactNode) => {
      if (!isValidElement(child)) {
        return;
      }
      if (child.type === Fragment) {
        Children.forEach(child.props.children, processChildren);
      } else {
        processChildren(child);
      }
    });
  }, [children]);

  return null; // Render nothing
});
